import fetch from 'node-fetch';
import _ from 'lodash';
import debugLib from 'debug';
import config from '../config';
import { cachePromise } from '../util/decorators';
import synchronousFetch from '../util/synchronousFetch';
import sentryLog from '../universalSentryLog';

const debug = debugLib('vs3:service:external');
debug.log = console.info.bind(console);

const { VIAPLAY_BACKEND_URL, STREAMLINKS_URL, TECH_NOTIFICATIONS_URL, REGION_LOOKUP_URL, VIASPORT_EUI_API } = config;

const TIMEOUT = 5000;

const ONE_MINUTE = 60 * 1000;
const FIVE_MINUTES = 5 * 60 * 1000;
const THIRTY_MINUTES = 30 * 60 * 1000;

function fetchWrapper(url) {
  return fetch(url, { timeout: TIMEOUT })
    .then(response => {
      if (!response.ok) {
        const error = new Error(response.statusText || response.status);
        error.response = response;
        throw error;
      }
      return response;
    });
}

const api = {
  @cachePromise(FIVE_MINUTES)
  getViaplayLiveFeed(country, language, onlyHighlights) {
    const feed = onlyHighlights ? `vp-sports-highlights-${country}-feed` : `vp-sports-event-${country}-feed`;
    const url = `${VIAPLAY_BACKEND_URL}/programs/${feed}?country=${country}&lang=${language}`;

    debug(`getViaplayLiveFeed request: ${url}`);

    return fetchWrapper(url)
      .then(res => res.json())
      .then(feedList => feedList.sort((a, b) => {
        const n = new Date(a.utcStartDateTime);
        const m = new Date(b.utcStartDateTime);
        return n > m ? 1 : -1;
      }))
      .catch(err => {
        sentryLog('Failed to fetch from Viaplay API:', err);
        return [];
      });
  },

  @cachePromise(FIVE_MINUTES)
  getFacebookCount(url) {
    const empty = {
      share_count: 0,
      comment_count: 0,
    };

    return fetchWrapper(`https://graph.facebook.com/?id=http://${encodeURIComponent(url)}`)
      .then(res => res.json())
      .then(res => _.get(res, 'share', empty))
      .catch(() => {
        return empty;
      });
  },

  @cachePromise(FIVE_MINUTES)
  getTwitterCount(url) {
    return fetchWrapper(`https://opensharecount.com/count.json?url=${encodeURIComponent(url)}`)
      .then(res => res.json())
      .then(res => res.count || 0)
      .catch(err => {
        sentryLog('Failed to fetch twitter count:', err);
        return 0;
      });
  },

  @cachePromise(FIVE_MINUTES)
  getStreamLinks(locale, mediaGuid) {
    debug(`getStreamLinks request: ${STREAMLINKS_URL}/viasport/web/${locale}/clear-media-guids/${mediaGuid}/streams`);

    return fetchWrapper(`${STREAMLINKS_URL}/viasport/web/${locale}/clear-media-guids/${mediaGuid}/streams`)
      .then(res => res.json())
      .catch(err => {
        sentryLog('Failed to fetch from Stream Links API:', err);
        throw err;
      });
  },

  // This is used for getting video source on mobile by the videoplayer. Not recommended to use anywhere else.
  @cachePromise(FIVE_MINUTES)
  getStreamLinksSync(locale, mediaGuid) {
    debug(`getStreamLinksSync request: ${STREAMLINKS_URL}/viasport/web/${locale}/clear-media-guids/${mediaGuid}/streams`);
    return synchronousFetch(`${STREAMLINKS_URL}/viasport/web/${locale}/clear-media-guids/${mediaGuid}/streams`);
  },

  @cachePromise(ONE_MINUTE)
  getTechNotifications(locale) {
    const url = `${TECH_NOTIFICATIONS_URL}/viasport/${locale}/pc`;

    debug(`getTechNotifications request: ${url}`);

    return fetchWrapper(url)
      .then(res => res.json())
      .then(res => _.get(res, 'embedded.notifications'))
      .catch(err => {
        sentryLog('Failed to fetch from Tech Notifications API:', err);
        return [];
      });
  },

  @cachePromise(ONE_MINUTE)
  getUserRegion(country) {
    const url = REGION_LOOKUP_URL;

    debug(`getUserRegion request: ${url}`);

    return fetchWrapper(url)
      .then(res => res.json())
      .then(res => res.countryCode)
      .catch(err => {
        sentryLog('Failed to fetch from Region Lookup API:', err);
        return country;
      });
  },

  @cachePromise(THIRTY_MINUTES)
  getMidrollCuepoints(mediaGuid) {
    const url = `${VIASPORT_EUI_API}/cue-points/${mediaGuid}`;

    return fetchWrapper(url)
      .then(res => res.json())
      .catch(err => {
        sentryLog('Failed to fetch from EUI API:', err);
        return [];
      });
  },
};

export default api;



// WEBPACK FOOTER //
// ./src/service/external.js